import { PaymentMethods } from "@adyen/adyen-web"
import { Cart } from "@commercetools/platform-sdk"
import { ifBrowser } from "@sixty-six-north/ui-system"
import { useState } from "react"
import { useAdyenBrowserClient } from "../../../adyen/AdyenCheckoutHooks"
import {
  AdyenState,
  useSelectedAdyenPaymentType,
  useAdyenPaymentData,
  useAdyenPayment
} from "../../../adyen/AdyenState"
import { ErrorType } from "../../../error/ErrorType"
import { useStoreContext } from "../../../i18n/StoreHooks"
import { useAppDispatch } from "../../../State"
import logger from "../../../utils/logger"
import {
  ValidationStrategy,
  useValidationStrategy,
  success
} from "../../validation/FormValidator"
import { error, ValidationResult } from "../../validation/ValidationError"
import { PaymentProvider, PaymentProviderState } from "../PaymentProviderState"
import { useEmptyPaymentErrorMessage } from "./PaymentValidation"

type SupportedAdyenPaymentMethods = {
  [K in Extract<
    keyof PaymentMethods,
    "applepay" | "scheme" | "googlepay" | "paypal" | "klarna" | "klarna_account"
  >]: PaymentMethods[K]
}

export type AdyenPaymentMethodType = keyof SupportedAdyenPaymentMethods

export interface AdyenPaymentMethod {
  type: AdyenPaymentMethodType
  name: string
}

export const useBrowserPaymentSupport = () => {
  const defaultSupportFn = () => true
  const paymentMethodAvailability: { applepay: () => boolean } = {
    applepay: () => window.ApplePaySession !== undefined
  }

  return {
    supports: (type: AdyenPaymentMethodType): boolean => {
      const fn = paymentMethodAvailability[type] || defaultSupportFn
      return ifBrowser(fn) || false
    }
  }
}

export const useAdyenPaymentMethod = (
  paymentProviders: PaymentProvider[]
): ValidationStrategy & {
  initialise: (cart?: Cart) => void
  select: (type: string) => void
  paymentMethods: AdyenPaymentMethod[]
} => {
  const dispatch = useAppDispatch()

  const { isShoppingInIceland } = useStoreContext()
  const message = useEmptyPaymentErrorMessage()
  const selectedAdyenPaymentType = useSelectedAdyenPaymentType()
  const paymentData = useAdyenPaymentData()

  const [paymentMethods, setPaymentMethods] = useState<
    { name: string; type: AdyenPaymentMethodType }[]
  >([])
  const adyenPayment = useAdyenPayment() || { id: "", session: "" }

  const adyenBrowserClient = useAdyenBrowserClient()

  const initialise = (cart?: Cart) => {
    if (!cart || isShoppingInIceland()) return
    adyenBrowserClient
      .initialise(cart)
      .then(
        ({
          paymentId,
          paymentMethods: adyenPaymentMethods,
          session: adyenSession,
          amount
        }) => {
          if (paymentId && adyenSession) {
            dispatch(
              AdyenState.actions.setAdyenPayment({
                id: paymentId,
                session: adyenSession,
                paymentMethods: adyenPaymentMethods,
                amount
              })
            )
          }
          setPaymentMethods(adyenPaymentMethods)
        }
      )
      .catch(err => {
        logger.warn("Failed to initialise payment", err)
      })
  }

  const strategy = useValidationStrategy(
    () => paymentProviders.indexOf(PaymentProvider.Adyen) > -1,
    {
      name: "AdyenValidator",
      validate: () => {
        if (isShoppingInIceland()) {
          return error(ErrorType.PaymentError, message)
        }
        if (
          selectedAdyenPaymentType !== "scheme" ||
          paymentData !== undefined
        ) {
          return success()
        } else {
          return error(ErrorType.PaymentError, message)
        }
      },
      save(): ValidationResult {
        return Promise.resolve(
          dispatch(
            PaymentProviderState.actions.addPaymentProvider(
              PaymentProvider.Adyen
            )
          )
        )
          .then(() => {
            try {
              adyenBrowserClient.paymentMethod(
                adyenPayment.id,
                selectedAdyenPaymentType as AdyenPaymentMethodType
              )
            } catch (err) {
              logger.warn(err)
            }
          })
          .then(() => success())
      }
    }
  )

  const select = (type: string) => {
    dispatch(AdyenState.actions.setAdyenPaymentType(type))
  }

  return {
    ...strategy,
    initialise,
    select,
    paymentMethods
  }
}
